import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, debounceTime, filter, map } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EPermissionStatus, Permission, PermittedCommand, PermittedThs } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectPermissions, selectPermittedCommands, selectPermittedThs } from 'src/app/store/permissions/permissions.selector';
import { TranslateService } from '@ngx-translate/core';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { PermissionDialogComponent } from '../../permission-dialog/permission.dialog.component';
import { PermissionEmailDialogComponent } from '../../permission-email-dialog/permission-email.dialog.component';
import { CommonCustomConfirmDialogComponent } from 'src/app/modules/shared/components/common-custom-confirm-dialog/common-custom-confirm.dialog.component';

export interface PermissionsListFilters {
	filter: string;
	activeFilter: boolean;
	readyFilter: boolean;
	expiredFilter: boolean;
	createdFilter: boolean;
}

@Component({
	selector: 'app-permissions-list-filters',
	templateUrl: './permissions-list-filters.component.html',
	providers: [ConfirmationService, DialogService]
})

export class PermissionsListFiltersComponent implements OnInit, OnDestroy {
	@Output() filtersChanged = new EventEmitter<PermissionsListFilters>();

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;

	permissions$: Observable<Permission[] | undefined>;
	permissionsSub: Subscription | undefined;

	permittedCommands$: Observable<PermittedCommand[] | undefined>;
	permittedCommandsSub: Subscription | undefined;

	permittedThs$: Observable<PermittedThs[] | undefined>;
	permittedThsSub: Subscription | undefined;

	filterSub: Subscription | undefined;
	activeFilterSub: Subscription | undefined;
	readyFilterSub: Subscription | undefined;
	expiredFilterSub: Subscription | undefined;
	createdFilterSub: Subscription | undefined;

	site: Site;
	permissions: Permission[] = [];
	permittedCommands: PermittedCommand[] = [];
	permittedThs: PermittedThs[] = [];

	filterForm: FormGroup;
	filter = '';
	activeFilter = true;
	readyFilter = true;
	expiredFilter = true;
	createdFilter = true;

	ref: DynamicDialogRef | undefined;
	acceptButtonStyle: string;
	rejectButtonStyle: string;
	expiredPermissionsCount = 0;

	// Design
	screenWidth: number;
	COLUMN_TITLE_MIN_WD = 647;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;
	MIN_WD_COL4_MTMA = StandardDesignParameters.MIN_WD_COL4_MTMA;

	constructor(
		private store: Store<AppState>,
		private formBuilder: FormBuilder,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.permissions$ = this.store.select(selectPermissions);
		this.permittedCommands$ = this.store.select(selectPermittedCommands);
		this.permittedThs$ = this.store.select(selectPermittedThs);
	}

	ngOnInit(): void {
		this.filterForm = this.formBuilder.group({
			searchFilter: [''],
			activeFilter: true,
			readyFilter: true,
			expiredFilter: true,
			createdFilter: true,
		});

		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
		this.siteSub?.unsubscribe();
		this.permissionsSub?.unsubscribe();
		this.permittedCommandsSub?.unsubscribe();
		this.permittedThsSub?.unsubscribe();
		this.filterSub?.unsubscribe();
		this.activeFilterSub?.unsubscribe();
		this.readyFilterSub?.unsubscribe();
		this.expiredFilterSub?.unsubscribe();
		this.createdFilterSub?.unsubscribe();
	}

	subscribeAll(): void {
		this.siteSub = this.site$.pipe(filter(s => !!s)).subscribe((site) => {
			this.site = site;
		});
		this.permissionsSub = this.permissions$?.subscribe((permissions) => {
			this.permissions = permissions ?? [];
			this.countExpiredPermissions();
		});
		this.permittedCommandsSub = this.permittedCommands$.subscribe((permittedCommands) => {
			this.permittedCommands = permittedCommands ?? [];
		});
		this.permittedThsSub = this.permittedThs$.subscribe((permittedThs) => {
			this.permittedThs = permittedThs ?? [];
		});

		this.filterSub = this.filterForm.controls['searchFilter'].valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.emitFilters();
		});

		this.activeFilterSub = this.filterForm.controls['activeFilter'].valueChanges.subscribe(value => {
			this.activeFilter = value;
			this.emitFilters();
		});

		this.readyFilterSub = this.filterForm.controls['readyFilter'].valueChanges.subscribe(value => {
			this.readyFilter = value;
			this.emitFilters();
		});

		this.expiredFilterSub = this.filterForm.controls['expiredFilter'].valueChanges.subscribe(value => {
			this.expiredFilter = value;
			this.emitFilters();
		});

		this.createdFilterSub = this.filterForm.controls['createdFilter'].valueChanges.subscribe(value => {
			this.createdFilter = value;
			this.emitFilters();
		});
	}

	private emitFilters(): void {
		this.filtersChanged.emit({
			filter: this.filter,
			activeFilter: this.activeFilter,
			readyFilter: this.readyFilter,
			expiredFilter: this.expiredFilter,
			createdFilter: this.createdFilter,
		});
	}

	countExpiredPermissions(): void {
		this.expiredPermissionsCount = this.permissions.filter(perm => perm.Status === EPermissionStatus.Expired).length;
	}

	anyPossibleCommand(): boolean {
		return this.permittedCommands?.length > 0 || this.permittedThs?.length > 0;
	}

	addPermission(): void {
		if (this.site?.Timezone === '') { // add permission not available due to site timezone
			this.dialogService.open(CommonCustomConfirmDialogComponent, {
				header: this.translate.instant('TIMEZONE_SITE_NOT_SET'),
				contentStyle: { overflow: 'auto' },
				data: {
					icon: 'cm-alert',
					row1: 'TIMEZONE_SITE_NOT_SET_WARNING',
					visibleAcceptButton: false,
					labelRejectButton: 'CLOSE',
				}
			});
			return;
		}

		if (this.anyPossibleCommand()) {
			this.store.dispatch(PermissionActions.resetPermissionForm());
			this.ref = this.dialogService.open(PermissionDialogComponent, {
				header: this.translate.instant('ADD_PERMISSION'),
				width: '70vw',
				breakpoints: { '1600px': '98vw' },
				contentStyle: { overflow: 'auto' },
				data: {}
			});
			return;
		}

		this.acceptButtonStyle = 'p-button-primary';
		this.rejectButtonStyle = 'p-button-secondary';
		this.confirmationService.confirm({
			header: this.translate.instant('WARNING'),
			message: this.translate.instant('FUNCTIONALITY_UNAVAILABLE'),
			acceptLabel: this.translate.instant('OK'),
			rejectLabel: this.translate.instant('CLOSE'),
			icon: 'pi cm-alert',
			reject: () => { }
		});
	}

	deleteExpired(): void {
		this.acceptButtonStyle = 'p-button-danger';
		this.rejectButtonStyle = 'p-button-secondary';
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_ALL_EXPIRES'),
			message: this.translate.instant('DELETE_PERMISSION_EXPIRED_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(PermissionActions.deletePermissionExpired({ siteId: this.site.Id }));
			},
			reject: () => { }
		});
	}

	isFinalUsers(): boolean {
		return this.site !== undefined && this.site !== null && !this.site.CurrentlyOwned;
	}

	customizeEmail(): void {
		this.store.dispatch(PermissionActions.resetPermissionTemplateForm());
		this.ref = this.dialogService.open(PermissionEmailDialogComponent, {
			header: this.translate.instant('CUSTOMIZE_EMAIL'),
			width: '80vw',
			breakpoints: { '1220px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {}
		});
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	col6HeaderPage(position: string): string {
		return StandardPageLayout.col6HeaderPage(this.screenWidth, position);
	}
}
